import React from 'react';
import styled from '@emotion/styled';
import { AppButton } from '../../../button/app-button';
import { spacing } from '../../../../style-utils/theme';
import { Field, FieldError, FieldHint, FieldRow, HelperText, Input } from '../../../form-kit';
import { formatMoney } from '../../../../utils/stays-money';
import {
  breakdownStrip,
  effectivePayout,
  FormContext,
  MoneyKey,
  percentLine,
} from './reservation-form-model';
import { describe, errorId, FieldKit, FieldLabel, hintId } from './reservation-fields';
import { FormSection, MoneyLine, SectionTitle, StaticLabel, StaticValue } from './reservation-form.styled';

/**
 * "AMOUNTS (FOR YOUR RECORDS)" (plan S4, R-S4.9). Nothing here is charged:
 * these are the figures of a booking taken elsewhere. Every amount is typed as
 * text and read with parseAmount() — no thousands separators, no comma
 * decimals, never rounded — and checked when the field is left.
 *
 * The payout follows the platform switch (Q31, policy.host_payout):
 *   typed      — an input, pre-filled with gross − fee − tax until the host
 *                types their own figure ("Use gross − fees" puts it back);
 *   calculated — read-only text; host_payout_cents is never sent.
 */

const Lead = styled(HelperText)`
  margin: 0 0 ${spacing.sm};
`;

export interface MoneyFieldsProps {
  kit: FieldKit;
  ctx: FormContext;
  headingId: string;
  /** 'percent' (New stay): "Fee 15 % · Payout 82 %"; 'strip' (Edit): the full breakdown. */
  summary: 'percent' | 'strip';
  /** The section's own id (the Edit modal scrolls to it for "Add amounts"). */
  sectionId?: string;
}

export const MoneyFields: React.FC<MoneyFieldsProps> = ({ kit, ctx, headingId, summary, sectionId }) => {
  const { form, errors, disabled, update, blur, id } = kit;
  const cur = ctx.currency;
  const line = summary === 'percent' ? percentLine(form, ctx) : breakdownStrip(form, ctx);

  const amount = (key: MoneyKey, label: string, hint?: string) => (
    <Field>
      <FieldLabel kit={kit} field={key} htmlFor={id(key)}>
        {label}
      </FieldLabel>
      <Input
        id={id(key)}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={form[key]}
        disabled={disabled}
        aria-invalid={errors[key] ? true : undefined}
        aria-describedby={describe(kit, key, !!hint)}
        hasError={!!errors[key]}
        onChange={event => update({ [key]: event.target.value } as Partial<typeof form>)}
        onBlur={() => {
          if (blur) blur(key);
        }}
      />
      {hint ? <FieldHint id={hintId(kit, key)}>{hint}</FieldHint> : null}
      <FieldError id={errorId(kit, key)} message={errors[key]} />
    </Field>
  );

  const calculated = effectivePayout(form, ctx);

  return (
    <FormSection $flat={kit.flat} aria-labelledby={headingId} id={sectionId}>
      <SectionTitle id={headingId}>Amounts (for your records)</SectionTitle>
      <Lead>Lobbero never charges the guest for the booking.</Lead>

      <FieldRow>
        {amount('gross_cents', `Total paid by the guest (${cur})`)}
        {amount('channel_fee_cents', `Channel fee (${cur})`)}
      </FieldRow>
      <FieldRow>
        {amount('tourist_tax_cents', `Tourist tax (${cur})`)}
        {ctx.mode === 'typed' ? (
          <Field>
            <FieldLabel kit={kit} field="host_payout_cents" htmlFor={id('host_payout_cents')}>
              {`Host payout (${cur})`}
            </FieldLabel>
            <Input
              id={id('host_payout_cents')}
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={form.host_payout_cents}
              disabled={disabled}
              aria-invalid={errors.host_payout_cents ? true : undefined}
              aria-describedby={describe(kit, 'host_payout_cents', true)}
              hasError={!!errors.host_payout_cents}
              onChange={event => update({ host_payout_cents: event.target.value })}
              onBlur={() => {
                if (blur) blur('host_payout_cents');
              }}
            />
            <FieldHint id={hintId(kit, 'host_payout_cents')}>
              {form.payout_edited ? 'Your own figure.' : 'Total − channel fee − tourist tax, until you change it.'}
            </FieldHint>
            {form.payout_edited ? (
              <div>
                <AppButton
                  variant="ghost"
                  size="compact"
                  disabled={disabled}
                  onClick={() => update({ payout_edited: false })}
                >
                  Use gross − fees
                </AppButton>
              </div>
            ) : null}
            <FieldError id={errorId(kit, 'host_payout_cents')} message={errors.host_payout_cents} />
          </Field>
        ) : (
          <Field>
            <StaticLabel id={`${id('host_payout_cents')}-label`}>{`Host payout (${cur})`}</StaticLabel>
            <StaticValue
              id={id('host_payout_cents')}
              aria-labelledby={`${id('host_payout_cents')}-label`}
              data-testid="stay-payout-calculated"
            >
              {calculated === null ? '—' : formatMoney(calculated, cur)}
            </StaticValue>
            <FieldHint id={hintId(kit, 'host_payout_cents')}>Worked out as total − channel fee − tourist tax.</FieldHint>
          </Field>
        )}
      </FieldRow>
      <FieldRow>{amount('payment_recorded_cents', `Recorded as paid (${cur})`)}</FieldRow>

      <MoneyLine role="status" data-testid={summary === 'percent' ? 'stay-money-percent' : 'stay-money-strip'}>
        {line}
      </MoneyLine>
    </FormSection>
  );
};
